import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchPendingOrders,
  setFilters,
  setRealTimeUpdates,
  fetchEscalationReasons,
  fetchEscalationRecipients
} from '../../redux/slices/pendingOrdersApiSlice';
import { pendingOrdersApiService } from '../../services/pendingOrdersApiService';
import OrderDetailsModal from './OrderDetailsModal';
import OrderEscalationModal from './OrderEscalationModal';
import toast from 'react-hot-toast';
import {
  MdSearch,
  MdMoreVert,
  MdAccessTime,
  MdWarning,
  MdFlag,
  MdOutlineKeyboardBackspace
} from 'react-icons/md';
import { Link } from 'react-router-dom';
import { FaAngleRight } from 'react-icons/fa6';

const PendingOrders: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    filteredOrders,
    summaryStats,
    loading,
    error,
    filters,
  } = useAppSelector(state => state.pendingOrdersApi);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [escalatingOrderId, setEscalatingOrderId] = useState<string | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    console.log('🚀 Component: Dispatching fetchPendingOrders...');
    dispatch(fetchPendingOrders());
    dispatch(fetchEscalationReasons());
    dispatch(fetchEscalationRecipients());
  }, [dispatch]);

  // Real-time updates
  useEffect(() => {
    console.log('🔄 Component: Starting real-time updates...');
    const cleanup = pendingOrdersApiService.startRealTimeUpdates((orders) => {
      console.log('🔄 Component: Received real-time update:', orders.length, 'orders');
      dispatch(setRealTimeUpdates(orders));
    });
    return cleanup;
  }, [dispatch]);

  // Handle search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchTerm !== filters.customer) {
        dispatch(setFilters({ ...filters, customer: searchTerm || undefined }));
      }
    }, 300);
    return () => clearTimeout(delayedSearch);
  }, [searchTerm, filters, dispatch]);

  // Error handling
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);


  const formatWaitTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}min:${String(minutes % 60).padStart(2, '0')}secs`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString()}`;
  };
  const getWaitTimeBgColor = (minutes: number) => {
    if (minutes >= 30) return 'bg-red-500 text-white';
    if (minutes >= 15) return 'bg-orange-500 text-white';
    return 'bg-gray-200 text-gray-800';
  };

  const getStatusBadgeColor = (status: string, minutes: number) => {
    if (status === 'escalated') return 'bg-red-500 text-white';
    if (status === 'flagged') return 'bg-orange-500 text-white';
    if (minutes >= 30) return 'bg-red-500 text-white';
    if (minutes >= 15) return 'bg-orange-500 text-white';
    return 'bg-gray-200 text-gray-800';
  };

  // Summary stats cards matching the design
  const statsCards = [
    {
      title: 'Total Pending',
      value: summaryStats.totalPending,
     
      color: 'text-black',
      
    },
    {
      title: 'Late Order',
      value: summaryStats.lateOrders,
    
      color: 'text-yellow-600',
     
    },
    {
      title: 'Escalated',
      value: summaryStats.escalated,
      
      color: 'text-red-600',
     
    },
    {
      title: 'Avg Wait Time',
      value: `${summaryStats.averageWaitTime}mins`,
   
      color: 'text-black',
     
    }
  ];

  return (
     <section className="font-mont">
      {/* Header with Back Navigation */}

 <div className="flex items-start gap-4">
        <Link to="/dashboard/restaurants">
          <button className="cursor-pointer p-2 rounded-full bg-[#008B5014]">
            <MdOutlineKeyboardBackspace  className="w-6 h-6" />
          </button>
        </Link>
        <article>
          <h1 className="text-2xl font-medium  inline-flex items-center gap-3">
            <span className="font-semibold">Pending Orders</span> <FaAngleRight />
            Order Monitoring
          </h1>
          <p className="text-sm">
            <Link to="/dashboard/restaurants/m">
              Monitor orders awaiting vendor acceptance with real-time tracking and escalation controls.
            </Link>
          </p>
        </article>
      </div>

      <div className="my-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {statsCards.map((stat, index) => (
            <div key={index} className={`p-4 rounded-lg border`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  {stat.title === 'Total Pending' && (
                    <p className="text-xs mt-1">Orders awaiting acceptance</p>
                  )}
                  {stat.title === 'Late Order' && (
                    <p className="text-xs mt-1">Exceeded time threshold</p>
                  )}
                  {stat.title === 'Escalated' && (
                    <p className="text-xs  mt-1">Require immediate attention</p>
                  )}
                  {stat.title === 'Avg Wait Time' && (
                    <p className="text-xs mt-1">Average acceptance time</p>
                  )}
                </div>
                <div className={`p-2 rounded-lg`}>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search Vendors"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-80"
              />
            </div>
            
            {/* Status Filter Dropdowns */}
            <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
              <option>All Statuses</option>
              <option>Pending</option>
              <option>Flagged</option>
              <option>Escalated</option>
            </select>
            
            <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
              <option>All Restaurants</option>
              <option>Pizza Hut</option>
              <option>KFC Lagos</option>
              <option>Chicken Republic</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Order ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Restaurant</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Total</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Waiting Time</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2">Loading orders...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No orders found matching your criteria.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{order.orderNumber}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{order.customer.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{order.restaurant.name}</div>
                      <div className="text-xs text-gray-500">{order.restaurant.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{formatCurrency(order.total)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getWaitTimeBgColor(order.waitingTime)}`}>
                        <MdAccessTime className="w-3 h-3 mr-1" />
                        {formatWaitTime(order.waitingTime)} - {order.priority === 'high' ? 'High' : order.priority === 'critical' ? 'Critical' : 'Normal'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(order.status, order.waitingTime)}`}>
                        {order.status === 'escalated' && <MdFlag className="w-3 h-3 mr-1" />}
                        {order.status === 'flagged' && <MdWarning className="w-3 h-3 mr-1" />}
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        {order.status === 'escalated' ? ' - Critical' : 
                         order.status === 'flagged' ? ' - High' : 
                         order.waitingTime >= 30 ? ' - Critical' :
                         order.waitingTime >= 15 ? ' - High' : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        <div className="relative">
                          <button
                            onClick={() => setActiveDropdown(activeDropdown === order.id ? null : order.id)}
                            className="text-gray-600 hover:text-gray-800 p-1 rounded"
                          >
                            <MdMoreVert className="w-5 h-5" />
                          </button>
                          
                          {activeDropdown === order.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                              <button
                                onClick={() => {
                                  setSelectedOrderId(order.id);
                                  setActiveDropdown(null);
                                }}
                                className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <span>View Details</span>
                              </button>
                              <button
                                onClick={() => {
                                  // Handle Track action
                                  setActiveDropdown(null);
                                }}
                                className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <span>Track</span>
                              </button>
                              {!order.isEscalated && (
                                <button
                                  onClick={() => {
                                    setEscalatingOrderId(order.id);
                                    setActiveDropdown(null);
                                  }}
                                  className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <span>Escalate</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {selectedOrderId && (
        <OrderDetailsModal
         order={null}
          toggleModal={() => setSelectedOrderId(null)}
        />
      )}

      {escalatingOrderId && (
        <OrderEscalationModal
          orderId={escalatingOrderId}
          onClose={() => setEscalatingOrderId(null)}
        />
      )}

      {/* Click outside to close dropdown */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </section>
  );
};

export default PendingOrders;